import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { capture } from "@pontimed/analytics/client";
import { ApiError, apiFetch, getApiBaseUrl } from "@pontimed/api-client";
import { adoptNativeSession, sessionQueryKey, userProfileQueryKey } from "@pontimed/auth-client";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { CheckCircle2, ChevronDown, Fingerprint, LoaderCircle } from "lucide-react";

import { getAuthIntentCopy, type AuthIntent } from "@/components/auth/auth-modal-intents";
import {
  canOfferMarketingOptIn,
  clearProfileCompletionMarker,
  fetchAuthProfile,
  isSyntheticEmail,
  markProfileCompletionPending,
  needsProfileCompletion,
  type AuthProfileSnapshot,
} from "@/components/auth/profile-completion";
import { useFirebasePhoneAuth } from "@/lib/firebase/use-firebase-phone-auth";
import { useBrowsingCountry } from "@/lib/hooks/use-browsing-country";
import { clearCapturedReferralCode, getCapturedReferralCode } from "@/lib/referral/attribution";
import { clearCapturedAttribution, getCapturedAttribution } from "@/lib/attribution/attribution";
import {
  isEmailOtpEnabled,
  isPhoneOtpEnabled,
  useCountryConfig,
} from "@/lib/hooks/use-country-config";
import { showToast } from "@/lib/ui/toast";
import {
  enrollPasskey,
  hasPasskeyLoginOnThisDevice,
  isPasskeySupported,
  signInWithPasskey,
} from "@/lib/webauthn-client";

import { COUNTRY_PHONES, countryFlag, findCountryPhone } from "@/config/phone-prefixes";
import { useLegalSheet } from "@/components/legal/legal-sheet-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { Switch } from "@/components/ui/switch";

const COUNTRY_STORAGE_KEY = "pontimed_residence_country";

/**
 * Resolve the user's likely residence country at modal-open time.
 *
 *   1. localStorage (set on previous visits)
 *   2. the browsing country the user just picked in the country gate (P4) —
 *      so the sign-in sheet never asks for a different country than the one
 *      chosen a moment earlier
 *   3. navigator.language → ISO alpha-2
 *   4. fallback "ro"
 *
 * apps/api proxy + Cloudflare can also provide `CF-IPCountry` server-side; the
 * portal SPA doesn't see request headers, so geo-via-IP belongs in the API.
 * For now we lean on stored preference + browser locale, which gets the user
 * to a near-correct default in one tap.
 */
function detectInitialCountry(browsingCountry?: string | null): string {
  if (typeof window === "undefined") return "ro";
  try {
    const stored = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (stored && COUNTRY_PHONES.some((c) => c.code === stored)) return stored;
  } catch {
    /* localStorage may throw in Safari private mode */
  }
  const browsing = browsingCountry?.toLowerCase();
  if (browsing && COUNTRY_PHONES.some((c) => c.code === browsing)) return browsing;
  const lang = navigator.language || "";
  const region = lang.split("-")[1]?.toLowerCase() ?? lang.toLowerCase();
  if (region && COUNTRY_PHONES.some((c) => c.code === region)) return region;
  return "ro";
}

/** Renders below the methods view as a low-emphasis link. Used by blocking
 *  gates (auth-required routes) to give the user an obvious path back to
 *  public content (typically /find) when they don't want to sign in. */
export interface AuthModalEscapeAction {
  label: ReactNode;
  onClick: () => void;
}

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  /** Fires once auth completes, BEFORE onClose. Lets the provider distinguish
   *  "user dismissed" (only onClose) from "auth succeeded" (onAuthSuccess →
   *  onClose). Without this signal we can't fire a dismiss-only side effect
   *  like redirecting an unauthenticated route guard back to /find. */
  onAuthSuccess?: () => void;
  /** Path to navigate to after successful sign-in. */
  returnTo: string;
  /** Optional intent — drives the contextual benefit banner above the methods
   *  view. Banner is hidden on the OTP confirmation steps. */
  intent?: AuthIntent;
  /** When false, the user cannot close the modal — X, backdrop, ESC, drag
   *  are all suppressed. Pair with `escapeAction` so they still have a clear
   *  way out. Defaults to true. */
  dismissible?: boolean;
  escapeAction?: AuthModalEscapeAction;
  /** Non-null when the provider re-opened the modal AFTER an external auth
   *  round-trip (OAuth consent screen, magic link in the mail client) that
   *  produced a User row without a Profile. Opens straight on the profile
   *  step and seeds which contact channels are already verified, so we only
   *  ask for what's actually missing. See `profile-completion.ts`. */
  profileResume?: AuthProfileSnapshot | null;
  /** Opens on the email-code sign-in with this address filled in — the
   *  Facebook pending-link flow (`/auth/error`), where the account to sign in
   *  to is already known. */
  initialEmail?: string;
}

type Step = "methods" | "phone-otp" | "email-sent" | "profile-complete" | "biometric-offer";

// Per-device "don't ask me again" for the post-login biometrics offer. Local
// only — it's a nag suppressor, not account state.
const BIOMETRIC_OFFER_DISMISSED = "pontimed_biometric_offer_dismissed";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AvailableProviders {
  google?: boolean;
  apple?: boolean;
  facebook?: boolean;
}

/**
 * Module-level cache for `/api/auth/available-providers`. The endpoint is
 * idempotent + cheap; one fetch per page load covers every modal mount.
 * Failure → assume all three available rather than hiding the OAuth section.
 */
let providersPromise: Promise<AvailableProviders> | null = null;
function fetchAvailableProviders(): Promise<AvailableProviders> {
  if (providersPromise) return providersPromise;
  providersPromise = apiFetch
    .get<AvailableProviders>("/api/auth/available-providers")
    .catch(() => ({ google: true, apple: true, facebook: true }) as AvailableProviders);
  return providersPromise;
}

/**
 * Portal login modal. 1:1 port of the legacy `src/components/auth/auth-modal.tsx`
 * structure: a single "methods" step (social + sliding email/phone toggle),
 * the two OTP confirmation views (`phone-otp` + `email-sent`) and the
 * `profile-complete` step that creates the patient `Profile` row on a
 * brand-new signup (legacy `handleCompleteProfile`). Role selection stays
 * absent — the portal signs everyone up patient-default; provider/clinic
 * upgrades happen in `/account/user/settings`.
 *
 * Phone OTP uses Firebase Phone Auth (apps/api validates the idToken on
 * verify). Email uses the legacy combined endpoint that emails BOTH a magic
 * link AND a 6-digit code, so the user can pick whichever path is faster.
 */
export function AuthModal({
  open,
  onClose,
  onAuthSuccess,
  returnTo,
  intent,
  dismissible = true,
  escapeAction,
  profileResume = null,
  initialEmail,
}: AuthModalProps) {
  const { t, i18n } = useLingui();
  const firebasePhone = useFirebasePhoneAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { open: openLegal } = useLegalSheet();
  // Country-scoped channel gating (some markets disable SMS / email).
  // Reactive to residence country selector changes — no manual refetch.
  // (`countryCode` declared further down; the hook accepts a delayed value.)

  /**
   * Common post-auth handoff. Server has set the cookies; we now:
   *  1. Invalidate session + profile queries so `useSession` flips to
   *     "authenticated" and `useUserProfile` refetches.
   *  2. Close the modal.
   *  3. Soft-navigate to `returnTo` if it's a different path; otherwise
   *     stay put and let the current page re-render in place.
   *
   * No `window.location.reload()` — the search page keeps its scroll, its
   * filter state, its loaded results, and just swaps the auth-aware bits
   * (avatar with initials, save-search active, favorite buttons enabled).
   */
  const handleAuthSuccess = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: sessionQueryKey }),
      queryClient.invalidateQueries({ queryKey: userProfileQueryKey }),
    ]);
    onAuthSuccess?.();
    onClose();
    if (typeof window === "undefined") return;
    const currentPath = window.location.pathname + window.location.search;
    if (returnTo && returnTo !== currentPath) {
      void router.navigate({ to: returnTo });
    }
  };

  const [step, setStep] = useState<Step>("methods");
  const [otpMethod, setOtpMethod] = useState<"email" | "phone">("email");

  // Provider gating
  const [availableProviders, setAvailableProviders] = useState<AvailableProviders | null>(null);
  useEffect(() => {
    let cancelled = false;
    void fetchAvailableProviders().then((p) => {
      if (!cancelled) setAvailableProviders(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Form state — `countryCode` is the user's residence country, also used as
  // the default phone-prefix in the phone tab (one truth for both). Detected
  // from localStorage > navigator.language > fallback at modal-open; then
  // async-overridden by /api/geoip (CF `cf-ipcountry` → ipapi.co fallback)
  // unless the user has already touched the picker in this session.
  const { country: browsingCountry } = useBrowsingCountry();
  const [countryCode, setCountryCode] = useState<string>(() =>
    detectInitialCountry(browsingCountry),
  );

  /* Tracks whether the user manually flipped the country picker during
     the current modal session. Once true, /api/geoip results stop
     overriding the selection — the explicit pick wins. Reset every
     time the modal reopens so a different physical location triggers
     a fresh geo lookup. */
  const userPickedCountryRef = useRef(false);
  const handleCountryChange = useCallback((next: string) => {
    userPickedCountryRef.current = true;
    setCountryCode(next);
  }, []);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");

  // Profile step — the `phone` / `email` fields above double as the optional
  // "add your other contact" inputs there (whichever channel the user did NOT
  // sign in with is empty by definition, and we never walk back to the methods
  // step from the profile step).
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  /** Contact channels already verified on the server — hides the matching
   *  optional input and renders a "verified" chip instead (legacy parity). */
  const [hasVerifiedEmail, setHasVerifiedEmail] = useState(false);
  const [hasVerifiedPhone, setHasVerifiedPhone] = useState(false);
  /** D-R4 signup marketing opt-in. `offerMarketingOptIn` = the account has a
   *  real email (`canOfferMarketingOptIn`), resolved from the same snapshot as
   *  the two flags above; `marketingOptIn` = the user's tick. It starts — and
   *  after every close resets to — unticked, and never gates the step. */
  const [offerMarketingOptIn, setOfferMarketingOptIn] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // Per-country auth provider config — drives whether email / phone OTP
  // tabs are shown. Re-runs on residence country change (the query key
  // includes the upper-cased country code).
  const { data: countryConfig } = useCountryConfig(countryCode);
  const phoneEnabled = isPhoneOtpEnabled(countryConfig);
  const emailEnabled = isEmailOtpEnabled(countryConfig);

  // If the user picks a country that disables their currently-selected
  // channel, switch them onto the other one automatically.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- corrects the tab when the country's channel config arrives (async query), not derivable at render without losing the user's pick.
    if (otpMethod === "phone" && !phoneEnabled && emailEnabled) setOtpMethod("email");
    else if (otpMethod === "email" && !emailEnabled && phoneEnabled) setOtpMethod("phone");
  }, [otpMethod, phoneEnabled, emailEnabled]);

  // Re-detect each time the modal opens — covers users coming back after
  // changing language or visiting another country page. Also resets the
  // "user picked" flag so /api/geoip is allowed to override again.
  useEffect(() => {
    if (open) {
      userPickedCountryRef.current = false;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- per-open reset driven by the `open` prop; reads localStorage / navigator, which render must not.
      setCountryCode(detectInitialCountry(browsingCountry));
    }
    // Only the open transition re-detects; a browsing-country change while
    // the sheet is open must not overwrite the user's pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Entry step. Normally "methods"; a `profileResume` snapshot means the user
  // is already signed in (OAuth / magic-link round-trip) and only the Profile
  // row is missing, so we open directly on the profile step with the verified
  // channels pre-resolved.
  //
  // The "methods" reset only fires on a closed → open transition: several
  // callers (e.g. the booking route's auth gate) re-invoke `open()` while the
  // modal is already up, and that must never throw the user back to the start
  // of a flow they're in the middle of.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!open) return;
    if (profileResume) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- seeds the step from the `open` / `profileResume` props on a closed → open transition (see above).
      setHasVerifiedEmail(profileResume.emailVerified && !isSyntheticEmail(profileResume.email));
      setHasVerifiedPhone(profileResume.phoneVerified && !!profileResume.phone);
      setOfferMarketingOptIn(canOfferMarketingOptIn(profileResume));
      setStep("profile-complete");
      return;
    }
    if (justOpened) {
      setStep("methods");
      if (initialEmail) {
        setOtpMethod("email");
        setEmail(initialEmail);
      }
    }
  }, [open, profileResume, initialEmail]);

  /* IP-based country override on modal open. Uses /api/geoip (CF
     `cf-ipcountry` → ipapi.co fallback) so the picker reflects where
     the user actually is right now, not the last stored choice / browser
     locale. Skipped if the user has already flipped the picker in this
     session (their explicit pick wins). Silent on failure — the
     synchronous fallback already gave us a usable default. */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void apiFetch
      .get<{ country?: string | null }>("/api/geoip")
      .then((data) => {
        if (cancelled || userPickedCountryRef.current) return;
        const next = data.country?.toLowerCase();
        if (next && COUNTRY_PHONES.some((p) => p.code === next)) {
          setCountryCode(next);
        }
      })
      .catch(() => {
        /* keep the sync fallback if geoip is unreachable */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Persist residence selection so we recall it next time even if the user
  // never finishes signing in. Cheap one-line write — Safari private mode is
  // the only failure case and we silently ignore it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, countryCode);
    } catch {
      /* private mode */
    }
  }, [countryCode]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [socialLoading, setSocialLoading] = useState<"google" | "apple" | "facebook" | null>(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  // True when /login/verify returned 404 — device has a platform passkey but
  // it isn't enrolled with this RP. Drives the friendly inline guidance card
  // in MethodsView (instead of a destructive error banner).
  const [passkeyNoCredential, setPasskeyNoCredential] = useState(false);
  const [biometricEnrolling, setBiometricEnrolling] = useState(false);
  // null = probing, true/false = decided. Hide the passkey button until we
  // know — avoids a flicker on unsupported browsers (FF on iOS, etc.).
  const [passkeySupported, setPasskeySupported] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    void isPasskeySupported().then((ok) => {
      if (!cancelled) setPasskeySupported(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const otpTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // "Remember me" — when checked, refresh-token TTL extends from 7d to 30d.
  // Default ON: matches user expectation that closing the tab shouldn't log
  // them out within hours. Persisted across modal opens via localStorage.
  const [rememberMe, setRememberMe] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    try {
      const stored = window.localStorage.getItem("pontimed_remember_me");
      return stored === null ? true : stored === "1";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("pontimed_remember_me", rememberMe ? "1" : "0");
    } catch {
      /* private mode */
    }
  }, [rememberMe]);

  const country = useMemo(() => findCountryPhone(countryCode), [countryCode]);
  const phoneE164 = useMemo(() => {
    const local = phone.replace(/\D/g, "");
    return local ? `${country.prefix}${local}` : "";
  }, [country.prefix, phone]);

  // Reset state on close (defer to allow exit animation to play)
  useEffect(() => {
    if (open) return;
    const id = setTimeout(() => {
      setStep("methods");
      setPhone("");
      setEmail("");
      setPhoneOtp("");
      setEmailOtp("");
      setFirstName("");
      setLastName("");
      setHasVerifiedEmail(false);
      setHasVerifiedPhone(false);
      setOfferMarketingOptIn(false);
      setMarketingOptIn(false);
      setError("");
      setPasskeyNoCredential(false);
      setBiometricEnrolling(false);
      setLoading(false);
      setSocialLoading(null);
      setOtpCountdown(0);
      if (otpTimerRef.current) clearInterval(otpTimerRef.current);
      firebasePhone.cleanup();
    }, 200);
    return () => clearTimeout(id);
  }, [open, firebasePhone]);

  // OTP countdown — 300s (5 min) ticking down once /send returns success.
  useEffect(() => {
    if (otpCountdown <= 0) return;
    otpTimerRef.current = setInterval(() => {
      setOtpCountdown((c) => {
        if (c <= 1) {
          if (otpTimerRef.current) clearInterval(otpTimerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => {
      if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    };
  }, [otpCountdown]);

  const isLogin = false; // No way for the modal trigger to currently signal "login vs register". Match legacy default.

  /* ─────────────── Handlers ─────────────── */

  /**
   * Should we offer to turn on biometric sign-in right after a successful
   * non-passkey login?
   *
   * This is the ONLY place the capability gets bootstrapped. Enrolling from
   * Medical Vault grants `vault_unlock` only, and the "Sign-in" toggle that
   * would add `account_login` is itself disabled until a credential exists —
   * so a user who never passes through here can never make the biometric
   * button work, however many times they tap it.
   *
   * We stay quiet unless every condition holds: the hardware can do it, this
   * device has never completed a passkey login, the account hasn't already
   * enabled sign-in elsewhere, and the user hasn't waved us off before. Any
   * uncertainty (offline, endpoint failure) resolves to NOT asking — a missed
   * offer is cheap, a nag on every login is not.
   */
  const shouldOfferBiometrics = async (): Promise<boolean> => {
    if (passkeySupported !== true) return false;
    if (hasPasskeyLoginOnThisDevice()) return false;
    try {
      if (window.localStorage.getItem(BIOMETRIC_OFFER_DISMISSED) === "1") return false;
    } catch {
      /* private mode — fall through and offer */
    }
    try {
      const status = await apiFetch.get<{
        signInEnabled?: boolean;
        stepUpRequired?: boolean;
        hasPin?: boolean;
      }>("/api/account/vault-security");
      // An account with 2FA enrols a passkey only under a recent 2FA check —
      // right after an email / SMS sign-in it has none, and the enrolment
      // would bounce to the challenge; and the server enrols passkeys only on
      // an account with a vault PIN. Don't offer what would fail. (With the
      // vault locked, as it is right after sign-in, the passkey is registered
      // for sign-in only — the vault keeps asking for its PIN.)
      return (
        status?.signInEnabled !== true && status?.stepUpRequired !== true && status?.hasPin === true
      );
    } catch {
      return false;
    }
  };

  const handleEnableBiometrics = async () => {
    setError("");
    setBiometricEnrolling(true);
    try {
      // `intent: "sign_in"` is what grants `account_login` on a first
      // credential — without it this enrollment would be vault-only and the
      // login button would still 404.
      const ok = await enrollPasskey(undefined, "sign_in");
      if (!ok) return; // cancelled the OS sheet — leave them on the offer
      await handleAuthSuccess();
    } catch {
      setError(t`Could not set up biometrics. You can enable it later in Settings.`);
    } finally {
      setBiometricEnrolling(false);
    }
  };

  const handleSkipBiometrics = () => {
    try {
      window.localStorage.setItem(BIOMETRIC_OFFER_DISMISSED, "1");
    } catch {
      /* private mode — they'll be asked again next time */
    }
    void handleAuthSuccess();
  };

  /**
   * Post-verification fork — restores the legacy behaviour of
   * `src/components/auth/auth-modal.tsx` (handleVerifyOtp /
   * handleVerifyEmailOtp): ask the server whether this identity already has a
   * profile. Returning user → straight into the app, exactly as before. Brand
   * new signup → the profile step, which is the ONLY place a `Profile` row is
   * created at signup time (bookings, waitlist joins and family members all
   * resolve `User.profileId`, and reject when it's null).
   *
   * Any failure to read the profile falls back to the plain success handoff —
   * we never trap the user behind a step we couldn't justify.
   */
  const finishAuth = async () => {
    // This modal owns the signup from here on; drop the round-trip marker so
    // the provider-level resume can't fire a second prompt on top of us.
    clearProfileCompletionMarker();
    const snapshot = await fetchAuthProfile();
    if (!snapshot || !needsProfileCompletion(snapshot)) {
      if (await shouldOfferBiometrics()) {
        // Authenticated already — tell the provider before parking on the
        // offer, so dismissing it never reads as "signed in, then bailed".
        onAuthSuccess?.();
        setStep("biometric-offer");
        return;
      }
      await handleAuthSuccess();
      return;
    }
    setHasVerifiedEmail(snapshot.emailVerified && !isSyntheticEmail(snapshot.email));
    setHasVerifiedPhone(snapshot.phoneVerified && !!snapshot.phone);
    setOfferMarketingOptIn(canOfferMarketingOptIn(snapshot));
    // The user IS authenticated at this point — tell the provider now so
    // dismissing the profile step counts as "signed in, skipped setup" and
    // doesn't fire a route guard's `onDismiss` (bounce back to /find).
    onAuthSuccess?.();
    setStep("profile-complete");
  };

  // We bypass `useLogin()` here so each path can pass `country` to the
  // backend. The hook signatures don't accept country yet; once they do this
  // can fold back to `useLogin().loginWithOAuth(provider, { returnTo, country })`.
  const handleSocialLogin = (provider: "google" | "apple" | "facebook") => {
    if (socialLoading) return;
    capture("auth_method_selected", {
      method: provider,
      intent,
      country: countryCode,
      remember_me: rememberMe,
    });
    setSocialLoading(provider);
    // We leave the SPA entirely here, so the profile step can't run in this
    // mount. Drop a marker the provider picks up on return (legacy
    // `storeAuthIntent`) — otherwise an OAuth signup never gets a Profile row.
    markProfileCompletionPending();
    const base = getApiBaseUrl() || window.location.origin;
    const url = new URL(`/api/auth/oauth/${provider}/start`, base);
    // Absolute: the API only forwards an allow-listed absolute URL and silently
    // drops a relative one (the route guard hands us pathname + search), which
    // landed every OAuth sign-in on the dashboard instead of the page it began.
    if (returnTo) url.searchParams.set("return", new URL(returnTo, window.location.origin).href);
    url.searchParams.set("country", countryCode.toUpperCase());
    if (rememberMe) url.searchParams.set("remember", "1");
    window.location.assign(url.toString());
  };

  // Passwordless primary sign-in via a registered discoverable passkey. The
  // OS picker surfaces all eligible RP credentials; on success the server
  // mints session cookies and we hand off via handleAuthSuccess.
  //
  // 404 from /login/verify means the device offered a passkey but it isn't
  // enrolled with this RP — common when a first-time visitor taps the
  // biometric button. We surface that as a friendly guidance card in
  // MethodsView (passkeyNoCredential), not a red error banner, because the
  // alternative methods are right there below.
  const handlePasskeyLogin = async () => {
    if (passkeyLoading) return;
    capture("auth_method_selected", {
      method: "passkey",
      intent,
      country: countryCode,
      remember_me: rememberMe,
    });
    setError("");
    setPasskeyNoCredential(false);
    setPasskeyLoading(true);
    try {
      const ok = await signInWithPasskey(rememberMe);
      if (!ok) {
        // `false` means the OS call came back empty. WebAuthn reports "user
        // cancelled" and "this device holds no passkey for us" as the same
        // NotAllowedError, so we disambiguate with the device marker: if a
        // passkey login has never succeeded here, the honest read is "not set
        // up yet" and the user deserves the guidance card instead of a button
        // that appears to do nothing. If they HAVE signed in this way before,
        // it really was a cancel — stay silent.
        if (!hasPasskeyLoginOnThisDevice()) setPasskeyNoCredential(true);
        return;
      }
      await finishAuth();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setPasskeyNoCredential(true);
      } else if (e instanceof ApiError && e.status === 429) {
        setError(t`Too many attempts. Try again later.`);
      } else {
        setError(t`Could not sign in with this passkey. Try again.`);
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSendPhoneOtp = async () => {
    capture("auth_method_selected", {
      method: "phone",
      intent,
      country: countryCode,
      remember_me: rememberMe,
    });
    setError("");
    setLoading(true);
    try {
      await apiFetch.post(
        "/api/auth/otp/phone/request",
        { phoneE164, country: countryCode.toUpperCase() },
        { skipAuthRefresh: true },
      );
      // The country's own Firebase project, when it has one. The server
      // verifies the ID token with the per-country service account
      // (auth-otp.ts → @pontimed/firebase), so minting it against the global
      // default project would fail every phone login in that country with an
      // audience mismatch the moment a country is given its own project —
      // which the admin Countries screen already supports.
      const ok = await firebasePhone.sendOtp(
        phoneE164,
        "recaptcha-container",
        countryConfig?.firebaseClientConfig ?? null,
      );
      if (!ok) {
        setError(firebasePhone.error || t`Failed to send SMS. Try again.`);
        return;
      }
      setOtpCountdown(300);
      setStep("phone-otp");
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setError(t`Too many attempts. Try again later.`);
      } else {
        setError(t`Could not send code. Try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await firebasePhone.verifyOtp(phoneOtp);
      if (!result?.idToken) {
        setError(firebasePhone.error || t`Invalid code. Try again.`);
        setPhoneOtp("");
        return;
      }
      // The native app keeps the session it is answered with (a no-op on the web).
      await adoptNativeSession(
        await apiFetch.post<unknown>(
          "/api/auth/otp/phone/verify",
          {
            phoneE164,
            idToken: result.idToken,
            country: countryCode.toUpperCase(),
            rememberMe,
          },
          { skipAuthRefresh: true },
        ),
      );
      firebasePhone.cleanup();
      await finishAuth();
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setError(t`Too many attempts. Try again later.`);
      } else {
        setError(t`Invalid code. Try again.`);
      }
      setPhoneOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmailOtp = async () => {
    capture("auth_method_selected", {
      method: "email",
      intent,
      country: countryCode,
      remember_me: rememberMe,
    });
    setError("");
    setLoading(true);
    try {
      // Legacy combined endpoint — emails BOTH magic link AND 6-digit code.
      // `locale` is the only signal apps/api has for which language to render
      // that email in (auth-email.ts -> otpEmailTemplate); without it the API
      // fell back to "ro" and non-Romanian users received a Romanian sign-in
      // email while mid-login.
      await apiFetch.post(
        "/api/auth/send-email-otp",
        // `rememberMe` rides with the REQUEST so the server can bake it into
        // the magic link itself. The link is opened from the mail client — a
        // tab that never saw this modal, so it cannot read the localStorage
        // choice. Without this every magic-link sign-in silently fell back to
        // the 7-day TTL even with the 30-day toggle on.
        // `next` likewise: the link lands on /auth/verify in that new tab, which
        // then follows it instead of always opening the dashboard.
        {
          email,
          country: countryCode.toUpperCase(),
          locale: i18n.locale,
          rememberMe,
          next: returnTo ? new URL(returnTo, window.location.origin).href : undefined,
        },
        { skipAuthRefresh: true },
      );
      // The magic link half of that email is usually opened in a NEW TAB from
      // the mail client, where this mount no longer exists — leave the resume
      // marker so the provider finishes the profile there. Cleared again by
      // `finishAuth()` if the user instead types the code right here.
      markProfileCompletionPending();
      setOtpCountdown(300);
      setStep("email-sent");
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setError(t`Too many attempts. Try again later.`);
      } else {
        setError(t`Could not send. Try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    setError("");
    setLoading(true);
    try {
      await adoptNativeSession(
        await apiFetch.post<unknown>(
          "/api/auth/verify-email-otp",
          { email, otp: emailOtp, country: countryCode.toUpperCase(), rememberMe },
          { skipAuthRefresh: true },
        ),
      );
      await finishAuth();
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        setError(t`Too many attempts. Try again later.`);
      } else {
        setError(t`Invalid code. Try again.`);
      }
      setEmailOtp("");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setError("");
    setOtpCountdown(0);
    if (step === "phone-otp") {
      setPhoneOtp("");
      firebasePhone.cleanup();
    }
    if (step === "email-sent") setEmailOtp("");
    setStep("methods");
  };

  /**
   * Creates the patient `Profile` row — port of legacy `handleCompleteProfile`.
   * Same endpoint, same payload keys, and always `role: "user"`: the portal
   * modal signs everyone up patient-default (provider / clinic workspaces are
   * created later from the account area, and `finishAuth` never routes those
   * userTypes here — the handler would downgrade them).
   *
   * `email` / `phone` are only sent for the channel the user did NOT sign in
   * with, matching legacy (`!hasVerifiedEmail ? email : undefined`).
   *
   * `marketingOptIn` (D-R4) is always an explicit boolean: the unticked-by-
   * default checkbox, forced to `false` when it wasn't offered. apps/api turns
   * a `true` into a `marketing_email` ConsentRecord (IP / UA / version) — the
   * same record Settings → "Promotional communications" reads and writes.
   */
  const handleCompleteProfile = async () => {
    setError("");
    setLoading(true);
    try {
      const trimmedEmail = email.trim().toLowerCase();
      // Legacy guard: only forward a phone the user actually typed out
      // (>= 6 digits), never a half-entered prefix.
      const typedPhone = phone.replace(/\D/g, "").length >= 6 ? phoneE164 : "";
      // Only the NAME goes to complete-profile. The second contact channel the
      // user typed here is unverified, and an unverified identifier must never
      // become a login identifier (account pre-hijack). It is still forwarded
      // for the marketing lead; linking a verified channel happens in Settings
      // via /api/auth/link-identity.

      // Referral attribution: the code was captured on apps/web (`?r=`) and
      // reaches us through the shared `.pontimed.<tld>` cookie. It travels in
      // the BODY too so the Capacitor build (`file://`, no shared cookie jar)
      // is credited the same way. Historically nothing wrote that cookie at
      // all, which is why no referrer was ever credited for a signup.
      const refCode = getCapturedReferralCode();
      // UTM / click-id attribution rides the same path for the same reasons.
      const attribution = getCapturedAttribution();
      await apiFetch.post("/api/auth/complete-profile", {
        role: "user",
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: !hasVerifiedEmail && EMAIL_RE.test(trimmedEmail) ? trimmedEmail : undefined,
        phone: !hasVerifiedPhone && typedPhone ? typedPhone : undefined,
        countryCode: countryCode.toUpperCase(),
        refCode,
        attribution,
        marketingOptIn: offerMarketingOptIn && marketingOptIn,
      });
      if (refCode) clearCapturedReferralCode();
      if (attribution) clearCapturedAttribution();
      capture("profile_completed", { intent, country: countryCode });
      showToast(t`Profile saved`, "success");
      // No token rotation needed: apps/api access tokens carry userId / role /
      // scope only (profileId is resolved from the DB per request), and the
      // role is unchanged by the patient branch. The session + profile query
      // invalidation inside handleAuthSuccess is what the UI actually needs.
      //
      // Fresh signups route through here rather than the finishAuth fork, so
      // the biometrics offer has to be repeated — this is the cohort most
      // likely to want it, and the only other chance is a toggle buried in
      // Medical Vault.
      if (await shouldOfferBiometrics()) {
        setError("");
        setStep("biometric-offer");
        return;
      }
      await handleAuthSuccess();
    } catch (e) {
      // 409 = a profile appeared meanwhile (parallel tab, retry after a
      // timeout). Nothing left to do — let the user into the app rather than
      // stranding them on a step they can no longer complete.
      if (e instanceof ApiError && e.status === 409) {
        await handleAuthSuccess();
        return;
      }
      if (e instanceof ApiError && e.status === 400) {
        setError(t`Please enter your first and last name.`);
      } else {
        setError(t`Could not save your profile. Try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Dismissal handler. On the profile step the user is ALREADY signed in, so
   * closing means "skip setup", not "cancel sign-in": run the normal success
   * handoff (invalidate session + profile queries, navigate to `returnTo`) so
   * they land in the app authenticated instead of on a gated skeleton.
   */
  const handleClose = () => {
    if (step === "profile-complete") {
      void handleAuthSuccess();
      return;
    }
    // Same rule on the biometrics offer: the user is already signed in, so
    // dismissing is "not now", not "cancel". Remember the refusal so we don't
    // ask again on this device.
    if (step === "biometric-offer") {
      handleSkipBiometrics();
      return;
    }
    onClose();
  };

  /* ─────────────── Render ─────────────── */

  const dialogTitle =
    step === "methods" ? (
      isLogin ? (
        <Trans>Sign in</Trans>
      ) : (
        <Trans>Sign in or create account</Trans>
      )
    ) : step === "phone-otp" ? (
      <Trans>Verify your phone</Trans>
    ) : step === "profile-complete" ? (
      <Trans>Almost there</Trans>
    ) : step === "biometric-offer" ? (
      <Trans>Sign in faster next time</Trans>
    ) : (
      <Trans>Check your email</Trans>
    );

  const dialogDescription =
    step === "methods" ? (
      isLogin ? (
        <Trans>Use Google, email, or phone — no password.</Trans>
      ) : (
        <Trans>One account for appointments, messages, and your medical journal.</Trans>
      )
    ) : step === "phone-otp" ? (
      <Trans>Enter the 6-digit code we just sent via SMS.</Trans>
    ) : step === "profile-complete" ? (
      <Trans>Tell us your name so doctors know who they're seeing.</Trans>
    ) : step === "biometric-offer" ? (
      <Trans>Use your fingerprint or face instead of a code.</Trans>
    ) : (
      <Trans>We sent a one-tap link and a 6-digit code to {email}.</Trans>
    );

  return (
    <ResponsiveSheet
      open={open}
      onClose={handleClose}
      width="md"
      title={dialogTitle}
      description={dialogDescription}
      // On the profile step the user is already authenticated, so a blocking
      // gate (`dismissible: false` from a route guard) must not keep them
      // locked in — closing there is a deliberate "skip setup" (handleClose).
      dismissible={step === "profile-complete" || step === "biometric-offer" ? true : dismissible}
      // The auth modal is purely imperative (opened via context, closed by
      // X/backdrop/auth-success). No URL state represents it. Skip the
      // history sentinel so it doesn't race with other ResponsiveSheets
      // (e.g. SearchUserMenu's AuthedPanel) that pop history.back() on
      // unmount when the logout flow flips `isAuthed` to false.
      disableHistory
    >
      <div className="flex flex-col gap-4">
        {step === "methods" && intent && <AuthIntentBanner intent={intent} />}
        {step === "methods" && (
          <MethodsView
            availableProviders={availableProviders}
            socialLoading={socialLoading}
            onSocialLogin={handleSocialLogin}
            passkeySupported={passkeySupported === true}
            passkeyLoading={passkeyLoading}
            passkeyNoCredential={passkeyNoCredential}
            onPasskeyLogin={handlePasskeyLogin}
            otpMethod={otpMethod}
            setOtpMethod={setOtpMethod}
            email={email}
            setEmail={setEmail}
            phone={phone}
            setPhone={setPhone}
            countryCode={countryCode}
            setCountryCode={handleCountryChange}
            phoneEnabled={phoneEnabled}
            emailEnabled={emailEnabled}
            error={error}
            loading={loading}
            onEmailSubmit={handleSendEmailOtp}
            onPhoneSubmit={handleSendPhoneOtp}
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
          />
        )}

        {step === "phone-otp" && (
          <OtpVerifyView
            otp={phoneOtp}
            setOtp={setPhoneOtp}
            countdown={otpCountdown}
            error={error}
            loading={loading}
            onSubmit={handleVerifyPhoneOtp}
            onResend={handleSendPhoneOtp}
            onBack={goBack}
          />
        )}

        {step === "email-sent" && (
          <EmailSentView
            emailOtp={emailOtp}
            setEmailOtp={setEmailOtp}
            countdown={otpCountdown}
            error={error}
            loading={loading}
            onVerifyOtp={handleVerifyEmailOtp}
            onResend={handleSendEmailOtp}
            onBack={goBack}
          />
        )}

        {step === "profile-complete" && (
          <ProfileCompleteView
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            email={email}
            setEmail={setEmail}
            phone={phone}
            setPhone={setPhone}
            countryCode={countryCode}
            setCountryCode={handleCountryChange}
            hasVerifiedEmail={hasVerifiedEmail}
            hasVerifiedPhone={hasVerifiedPhone}
            offerMarketingOptIn={offerMarketingOptIn}
            marketingOptIn={marketingOptIn}
            setMarketingOptIn={setMarketingOptIn}
            error={error}
            loading={loading}
            onSubmit={handleCompleteProfile}
          />
        )}

        {step === "biometric-offer" && (
          <BiometricOfferView
            error={error}
            enrolling={biometricEnrolling}
            onEnable={handleEnableBiometrics}
            onSkip={handleSkipBiometrics}
          />
        )}

        {step === "methods" && (
          <div className="space-y-1 text-center">
            <p className="text-muted-foreground text-[0.625rem]">
              <Trans>
                By continuing, you agree to PontiMed's{" "}
                <button
                  type="button"
                  onClick={() => openLegal("terms")}
                  className="text-primary relative after:absolute after:-inset-x-1 after:-inset-y-4 after:content-[''] hover:underline"
                >
                  Terms of Service
                </button>{" "}
                and{" "}
                <button
                  type="button"
                  onClick={() => openLegal("privacy")}
                  className="text-primary relative after:absolute after:-inset-x-1 after:-inset-y-4 after:content-[''] hover:underline"
                >
                  Privacy Policy
                </button>
                .
              </Trans>
            </p>
            <p className="text-muted-foreground text-[0.625rem]">
              <Trans>If you don't have an account yet, one will be created automatically.</Trans>
            </p>
          </div>
        )}

        {step === "methods" && escapeAction && (
          <button
            type="button"
            onClick={escapeAction.onClick}
            className="text-muted-foreground hover:text-foreground mx-auto min-h-11 px-3 text-xs underline-offset-4 hover:underline"
          >
            {escapeAction.label}
          </button>
        )}

        {/* Firebase reCAPTCHA host — required in DOM whenever the modal is
            mounted so RecaptchaVerifier can render into it on /sendOtp. */}
        <div id="recaptcha-container" />
      </div>
    </ResponsiveSheet>
  );
}

/* ─────────────── Methods step ─────────────── */

/**
 * Post-login offer to turn on biometric sign-in.
 *
 * Shown once per device, only to users who can actually use it and haven't
 * already enabled it. This is the bootstrap path for `account_login` — see
 * `shouldOfferBiometrics`. Declining is remembered locally; the setting stays
 * reachable afterwards from Medical Vault → Protected areas.
 */
function BiometricOfferView({
  error,
  enrolling,
  onEnable,
  onSkip,
}: {
  error: string;
  enrolling: boolean;
  onEnable: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="bg-primary-soft flex items-center justify-center rounded-xl py-6">
        <Fingerprint className="text-primary size-10" />
      </div>

      <p className="text-muted-foreground text-sm leading-relaxed">
        <Trans>
          Next time you can sign in with your fingerprint or face — no code, no waiting for an
          email. Your biometric data never leaves this device.
        </Trans>
      </p>

      {error && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      <div className="space-y-2">
        <Button className="h-12 w-full gap-2 text-base" disabled={enrolling} onClick={onEnable}>
          {enrolling ? (
            <LoaderCircle className="size-5 animate-spin" />
          ) : (
            <Fingerprint className="size-5" />
          )}
          <Trans>Turn on biometric sign-in</Trans>
        </Button>
        <Button
          variant="ghost"
          className="text-muted-foreground h-12 w-full text-base"
          disabled={enrolling}
          onClick={onSkip}
        >
          <Trans>Not now</Trans>
        </Button>
      </div>

      <p className="text-muted-foreground text-center text-xs">
        <Trans>You can change this anytime in Settings.</Trans>
      </p>
    </div>
  );
}

interface MethodsViewProps {
  availableProviders: AvailableProviders | null;
  socialLoading: "google" | "apple" | "facebook" | null;
  onSocialLogin: (p: "google" | "apple" | "facebook") => void;
  /** True when this device exposes a platform authenticator (Face ID / Touch
   *  ID / Windows Hello / Android biometric). The button is hidden when false
   *  — there's nothing to invoke. */
  passkeySupported: boolean;
  passkeyLoading: boolean;
  /** True when the last biometric attempt returned 404 (no passkey enrolled
   *  for this RP on this device). Renders an informational guidance card
   *  instead of the red error line. */
  passkeyNoCredential: boolean;
  onPasskeyLogin: () => void;
  otpMethod: "email" | "phone";
  setOtpMethod: (m: "email" | "phone") => void;
  email: string;
  setEmail: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  countryCode: string;
  setCountryCode: (c: string) => void;
  /** Whether the residence country has phone OTP enabled. */
  phoneEnabled: boolean;
  /** Whether the residence country has email OTP enabled. */
  emailEnabled: boolean;
  error: string;
  loading: boolean;
  onEmailSubmit: () => void;
  onPhoneSubmit: () => void;
  rememberMe: boolean;
  setRememberMe: (v: boolean) => void;
}

function MethodsView({
  availableProviders,
  socialLoading,
  onSocialLogin,
  passkeySupported,
  passkeyLoading,
  passkeyNoCredential,
  onPasskeyLogin,
  otpMethod,
  setOtpMethod,
  email,
  setEmail,
  phone,
  setPhone,
  countryCode,
  setCountryCode,
  phoneEnabled,
  emailEnabled,
  error,
  loading,
  onEmailSubmit,
  onPhoneSubmit,
  rememberMe,
  setRememberMe,
}: MethodsViewProps) {
  const { t } = useLingui();
  const showGoogle = availableProviders === null || availableProviders.google !== false;
  const showApple = availableProviders === null || availableProviders.apple !== false;
  const showFacebook = availableProviders === null || availableProviders.facebook !== false;
  const hasSocial = showGoogle || showApple || showFacebook || passkeySupported;

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const phoneValid = phone.replace(/\D/g, "").length >= 6;

  return (
    <div className="flex flex-col gap-3">
      {/* Country of residence — pre-filled by detection, drives both the
          phone-prefix tab default and the `country` payload sent to apps/api
          on signup. Compact row label + chip-style picker. */}
      <CountrySelector value={countryCode} onChange={setCountryCode} />

      {/* Passkey — rendered whenever the device exposes a platform
          authenticator, which says nothing about whether this user has a
          credential. A first-time visitor therefore sees it too; tapping it
          resolves false (empty OS picker) and we surface the guidance card
          below rather than failing silently. For an enrolled user it's the
          fastest path in the modal, which is why it stays on top. */}
      {passkeySupported && (
        <Button
          variant="outline"
          className="border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary h-12 w-full justify-start gap-3 text-base"
          disabled={passkeyLoading || !!socialLoading}
          onClick={onPasskeyLogin}
        >
          {passkeyLoading ? (
            <LoaderCircle className="size-5 animate-spin" />
          ) : (
            <Fingerprint className="size-5" />
          )}
          <Trans>Sign in with biometrics</Trans>
        </Button>
      )}

      {/* Friendly guidance card shown when /login/verify returns 404 — the
          device offered a passkey but it isn't enrolled with this RP (typical
          first-time visitor). The alternative methods are right below, so the
          card just nudges the user toward them. */}
      {passkeyNoCredential && (
        <div className="border-primary/30 bg-primary/5 space-y-2 rounded-lg border p-3">
          <div className="flex items-start gap-2">
            <Fingerprint className="text-primary mt-0.5 size-4 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-foreground text-sm font-medium">
                <Trans>Biometrics aren't set up yet</Trans>
              </p>
              <p className="text-muted-foreground text-xs leading-relaxed">
                <Trans>
                  This device isn't linked to a PontiMed account. Sign in with one of the methods
                  below — we'll save biometrics for next time.
                </Trans>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onPasskeyLogin}
            disabled={passkeyLoading}
            className="text-primary text-xs hover:underline disabled:opacity-60"
          >
            <Trans>Try biometrics again</Trans>
          </button>
        </div>
      )}

      {/* Social — colored brand SVGs to match legacy look. */}
      {showGoogle && (
        <Button
          variant="outline"
          className="h-12 w-full justify-start gap-3 text-base"
          disabled={!!socialLoading}
          onClick={() => onSocialLogin("google")}
        >
          {socialLoading === "google" ? (
            <LoaderCircle className="size-5 animate-spin" />
          ) : (
            <GoogleIcon />
          )}
          <Trans>Continue with Google</Trans>
        </Button>
      )}
      {showApple && (
        <Button
          variant="outline"
          className="h-12 w-full justify-start gap-3 text-base"
          disabled={!!socialLoading}
          onClick={() => onSocialLogin("apple")}
        >
          {socialLoading === "apple" ? (
            <LoaderCircle className="size-5 animate-spin" />
          ) : (
            <AppleIcon />
          )}
          <Trans>Continue with Apple</Trans>
        </Button>
      )}
      {showFacebook && (
        <Button
          variant="outline"
          className="h-12 w-full justify-start gap-3 text-base"
          disabled={!!socialLoading}
          onClick={() => onSocialLogin("facebook")}
        >
          {socialLoading === "facebook" ? (
            <LoaderCircle className="size-5 animate-spin" />
          ) : (
            <FacebookIcon />
          )}
          <Trans>Continue with Facebook</Trans>
        </Button>
      )}

      {/* OR divider — only when there's something on each side. */}
      {hasSocial && (
        <div className="flex items-center gap-3">
          <div className="bg-border h-px flex-1" />
          <span className="text-muted-foreground text-xs">
            <Trans>or</Trans>
          </span>
          <div className="bg-border h-px flex-1" />
        </div>
      )}

      {/* Sliding pill toggle — only shown when BOTH email + phone are
          enabled for the residence country. Single-channel countries skip
          the toggle and render the available panel directly. */}
      {emailEnabled && phoneEnabled && (
        <div className="bg-muted/50 relative flex rounded-lg p-1">
          <div
            className="bg-background ring-border absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-md shadow-sm ring-1 transition-transform duration-200 ease-out"
            style={{
              transform: otpMethod === "phone" ? "translateX(calc(100% + 4px))" : "translateX(0)",
            }}
            aria-hidden
          />
          <button
            type="button"
            onClick={() => setOtpMethod("email")}
            aria-pressed={otpMethod === "email"}
            className={`relative z-10 min-h-10 flex-1 rounded-md px-3 text-sm font-medium transition-colors ${
              otpMethod === "email"
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Trans>Email</Trans>
          </button>
          <button
            type="button"
            onClick={() => setOtpMethod("phone")}
            aria-pressed={otpMethod === "phone"}
            className={`relative z-10 min-h-10 flex-1 rounded-md px-3 text-sm font-medium transition-colors ${
              otpMethod === "phone"
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Trans>Phone</Trans>
          </button>
        </div>
      )}

      {/* Render whichever panel is active AND enabled for this country. */}
      {emailEnabled && (otpMethod === "email" || !phoneEnabled) && (
        <EmailMethod
          email={email}
          setEmail={setEmail}
          emailValid={emailValid}
          loading={loading}
          onSubmit={onEmailSubmit}
        />
      )}
      {phoneEnabled && (otpMethod === "phone" || !emailEnabled) && (
        <PhoneMethod
          phone={phone}
          setPhone={setPhone}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
          phoneValid={phoneValid}
          loading={loading}
          onSubmit={onPhoneSubmit}
        />
      )}
      {!emailEnabled && !phoneEnabled && (
        <p className="text-muted-foreground text-center text-xs">
          <Trans>Email and SMS sign-in are unavailable in your country.</Trans>
        </p>
      )}

      {/* "Keep me signed in" — checked by default. Off = 7 days,
          on = 30 days for the refresh token (and matching cookie maxAge). */}
      <label className="flex cursor-pointer items-center justify-between gap-3 select-none">
        <span className="text-foreground text-xs">
          <Trans>Keep me signed in for 30 days</Trans>
        </span>
        <Switch
          checked={rememberMe}
          onCheckedChange={(v) => setRememberMe(v === true)}
          aria-label={t`Keep me signed in for 30 days`}
        />
      </label>

      {error && <p className="text-destructive text-center text-xs">{error}</p>}
    </div>
  );
}

function EmailMethod({
  email,
  setEmail,
  emailValid,
  loading,
  onSubmit,
}: {
  email: string;
  setEmail: (v: string) => void;
  emailValid: boolean;
  loading: boolean;
  onSubmit: () => void;
}) {
  return (
    <div>
      <label htmlFor="auth-email" className="text-foreground mb-1 block text-xs font-medium">
        <Trans>Email</Trans>
      </label>
      <Input
        id="auth-email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && emailValid) onSubmit();
        }}
        placeholder="email@example.com"
        autoComplete="email"
        autoFocus
        className="h-12 text-base md:text-base"
      />
      {emailValid && (
        <Button className="mt-2 h-12 w-full text-base" onClick={onSubmit} disabled={loading}>
          {loading ? <LoaderCircle className="size-4 animate-spin" /> : <Trans>Send code</Trans>}
        </Button>
      )}
    </div>
  );
}

function PhoneMethod({
  phone,
  setPhone,
  countryCode,
  setCountryCode,
  phoneValid,
  loading,
  onSubmit,
}: {
  phone: string;
  setPhone: (v: string) => void;
  countryCode: string;
  setCountryCode: (c: string) => void;
  phoneValid: boolean;
  loading: boolean;
  onSubmit: () => void;
}) {
  return (
    <div>
      <label htmlFor="auth-phone" className="text-foreground mb-1 block text-xs font-medium">
        <Trans>Phone</Trans>
      </label>
      {/* 48px field like the email one and the OAuth buttons above it;
          PhoneInput's own controls are 32px, so size them from here. */}
      <PhoneInput
        id="auth-phone"
        value={phone}
        onChange={setPhone}
        countryCode={countryCode}
        onCountryChange={(code) => setCountryCode(code)}
        enterKeyHint="send"
        autoFocus
        className="[&>input]:placeholder:text-muted-foreground/70 [&>button]:h-12 [&>input]:h-12 [&>input]:text-base [&>input]:placeholder:opacity-100 md:[&>input]:text-base"
        onKeyDown={(e) => {
          if (e.key === "Enter" && phoneValid) onSubmit();
        }}
      />
      {phoneValid && (
        <Button className="mt-2 h-12 w-full text-base" onClick={onSubmit} disabled={loading}>
          {loading ? <LoaderCircle className="size-4 animate-spin" /> : <Trans>Send code</Trans>}
        </Button>
      )}
    </div>
  );
}

/* ─────────────── Phone OTP verify step ─────────────── */

function OtpVerifyView({
  otp,
  setOtp,
  countdown,
  error,
  loading,
  onSubmit,
  onResend,
  onBack,
}: {
  otp: string;
  setOtp: (v: string) => void;
  countdown: number;
  error: string;
  loading: boolean;
  onSubmit: () => void;
  onResend: () => void;
  onBack: () => void;
}) {
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const time = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <div className="flex flex-col gap-3">
      <Input
        type="text"
        inputMode="numeric"
        maxLength={6}
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
        onKeyDown={(e) => {
          if (e.key === "Enter" && otp.length >= 6) onSubmit();
        }}
        placeholder="000000"
        className="text-center text-2xl tracking-[0.5em]"
        autoComplete="one-time-code"
        autoFocus
      />

      {countdown > 0 && (
        <p className="text-muted-foreground text-center text-xs">
          <Trans>Code expires in {time}</Trans>
        </p>
      )}

      {error && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-destructive text-center text-xs">{error}</p>
          <button
            type="button"
            onClick={onResend}
            className="text-primary text-center text-xs font-medium hover:underline"
          >
            <Trans>Resend code</Trans>
          </button>
        </div>
      )}

      {!error && (
        <Button
          className="h-12 w-full text-base"
          onClick={onSubmit}
          disabled={loading || otp.length < 6}
        >
          {loading ? <LoaderCircle className="size-4 animate-spin" /> : <Trans>Verify code</Trans>}
        </Button>
      )}

      {!error && countdown === 0 && (
        <button
          type="button"
          onClick={onResend}
          className="text-primary text-center text-xs hover:underline"
        >
          <Trans>Resend code</Trans>
        </button>
      )}

      <button
        type="button"
        onClick={onBack}
        className="text-muted-foreground text-center text-xs hover:underline"
      >
        ← <Trans>Back</Trans>
      </button>
    </div>
  );
}

/* ─────────────── Email sent step ─────────────── */

function EmailSentView({
  emailOtp,
  setEmailOtp,
  countdown,
  error,
  loading,
  onVerifyOtp,
  onResend,
  onBack,
}: {
  emailOtp: string;
  setEmailOtp: (v: string) => void;
  countdown: number;
  error: string;
  loading: boolean;
  onVerifyOtp: () => void;
  onResend: () => void;
  onBack: () => void;
}) {
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const time = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label className="text-foreground mb-1.5 block text-xs font-medium">
          <Trans>Enter the 6-digit code</Trans>
        </label>
        <Input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={emailOtp}
          onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && emailOtp.length >= 6) onVerifyOtp();
          }}
          placeholder="000000"
          className="text-center text-2xl tracking-[0.5em]"
          autoComplete="one-time-code"
          autoFocus
        />
      </div>

      {countdown > 0 && (
        <p className="text-muted-foreground text-center text-xs">
          <Trans>Code expires in {time}</Trans>
        </p>
      )}

      {error && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-destructive text-center text-xs">{error}</p>
          <button
            type="button"
            onClick={onResend}
            className="text-primary text-center text-xs font-medium hover:underline"
          >
            <Trans>Resend code</Trans>
          </button>
        </div>
      )}

      {!error && (
        <Button
          className="h-12 w-full text-base"
          onClick={onVerifyOtp}
          disabled={loading || emailOtp.length < 6}
        >
          {loading ? <LoaderCircle className="size-4 animate-spin" /> : <Trans>Verify code</Trans>}
        </Button>
      )}

      {/* Or click magic link hint */}
      <div className="border-border/50 rounded-lg border p-3 text-center">
        <p className="text-muted-foreground text-xs">
          <Trans>Or just click the one-tap link in your inbox.</Trans>
        </p>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="text-muted-foreground text-xs hover:underline"
        >
          ← <Trans>Back</Trans>
        </button>
        {!error && countdown === 0 && (
          <button type="button" onClick={onResend} className="text-primary text-xs hover:underline">
            <Trans>Resend code</Trans>
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────── Profile completion step ─────────────── */

/**
 * Shown once, right after the FIRST successful verification of a brand-new
 * account (and on the OAuth / magic-link resume). Creates the patient
 * `Profile` row via `POST /api/auth/complete-profile` — without it the user
 * has no `profileId` and every booking, waitlist join and family member is
 * rejected by apps/api.
 *
 * Name is required (the server rejects a missing first/last name); the second
 * contact channel is optional and only rendered when it isn't verified yet,
 * matching the legacy step. Closing the sheet skips setup — the user stays
 * signed in and can fill this in later from Settings → Profile.
 *
 * The marketing opt-in (D-R4) renders only for accounts with a real email,
 * unticked; it never feeds `canSubmit`, so it can't block the step.
 */
function ProfileCompleteView({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  phone,
  setPhone,
  countryCode,
  setCountryCode,
  hasVerifiedEmail,
  hasVerifiedPhone,
  offerMarketingOptIn,
  marketingOptIn,
  setMarketingOptIn,
  error,
  loading,
  onSubmit,
}: {
  firstName: string;
  setFirstName: (v: string) => void;
  lastName: string;
  setLastName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  countryCode: string;
  setCountryCode: (c: string) => void;
  hasVerifiedEmail: boolean;
  hasVerifiedPhone: boolean;
  offerMarketingOptIn: boolean;
  marketingOptIn: boolean;
  setMarketingOptIn: (v: boolean) => void;
  error: string;
  loading: boolean;
  onSubmit: () => void;
}) {
  const canSubmit = firstName.trim().length > 0 && lastName.trim().length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label
          htmlFor="profile-first-name"
          className="text-foreground mb-1 block text-xs font-medium"
        >
          <Trans>First name</Trans>
        </label>
        <Input
          id="profile-first-name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit && !loading) onSubmit();
          }}
          autoComplete="given-name"
          enterKeyHint="next"
          autoFocus
        />
      </div>

      <div>
        <label
          htmlFor="profile-last-name"
          className="text-foreground mb-1 block text-xs font-medium"
        >
          <Trans>Last name</Trans>
        </label>
        <Input
          id="profile-last-name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit && !loading) onSubmit();
          }}
          autoComplete="family-name"
          enterKeyHint="done"
        />
      </div>

      {/* Second contact channel — optional, and only for the one the user did
          NOT sign in with. Verified channels render as a chip instead. */}
      {hasVerifiedEmail ? (
        <VerifiedContactChip label={<Trans>Email verified</Trans>} />
      ) : (
        <div>
          <label htmlFor="profile-email" className="text-foreground mb-1 block text-xs font-medium">
            <Trans>Email</Trans>{" "}
            <span className="text-muted-foreground font-normal">
              (<Trans>optional</Trans>)
            </span>
          </label>
          <Input
            id="profile-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            autoComplete="email"
          />
        </div>
      )}

      {hasVerifiedPhone ? (
        <VerifiedContactChip label={<Trans>Phone verified</Trans>} />
      ) : (
        <div>
          <label htmlFor="profile-phone" className="text-foreground mb-1 block text-xs font-medium">
            <Trans>Phone</Trans>{" "}
            <span className="text-muted-foreground font-normal">
              (<Trans>optional</Trans>)
            </span>
          </label>
          <PhoneInput
            id="profile-phone"
            value={phone}
            onChange={setPhone}
            countryCode={countryCode}
            onCountryChange={(code) => setCountryCode(code)}
            enterKeyHint="done"
          />
        </div>
      )}

      {/* Optional, unticked marketing opt-in — same checkbox pattern as the
          DPA / health-consent rows. Disabled mid-submit so the request can't
          carry a tick the user has just removed. */}
      {offerMarketingOptIn && (
        <label className="border-border hover:border-border-strong flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors">
          <input
            type="checkbox"
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
            disabled={loading}
            className="accent-primary mt-0.5 size-4 shrink-0"
          />
          <span className="text-foreground text-sm leading-snug">
            <Trans>Send me PontiMed news and tips by email. You can unsubscribe anytime.</Trans>
          </span>
        </label>
      )}

      {error && <p className="text-destructive text-center text-xs">{error}</p>}

      <Button
        className="mt-1 h-12 w-full text-base"
        onClick={onSubmit}
        disabled={loading || !canSubmit}
      >
        {loading ? <LoaderCircle className="size-4 animate-spin" /> : <Trans>Finish setup</Trans>}
      </Button>

      <p className="text-muted-foreground text-center text-[0.625rem]">
        <Trans>You can change this any time in your account settings.</Trans>
      </p>
    </div>
  );
}

function VerifiedContactChip({ label }: { label: ReactNode }) {
  return (
    <div className="bg-success-soft text-success-strong flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium">
      <CheckCircle2 className="size-4 shrink-0" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

/* ─────────────── Residence country selector ─────────────── */

function CountrySelector({ value, onChange }: { value: string; onChange: (code: string) => void }) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const country = findCountryPhone(value);

  useEffect(() => {
    if (!open) return;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (isMobile) return;
    const timer = setTimeout(() => searchRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  const filtered = search.trim()
    ? COUNTRY_PHONES.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.code.includes(search.toLowerCase()),
      )
    : COUNTRY_PHONES;

  return (
    <div className="flex items-center justify-between gap-3">
      <label className="text-foreground text-xs font-medium">
        <Trans>Country of residence</Trans>
      </label>
      <Popover
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setSearch("");
        }}
      >
        <PopoverTrigger className="border-input hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-ring/50 inline-flex h-8 cursor-pointer items-center gap-2 rounded-lg border bg-transparent px-2.5 text-sm transition-colors focus-visible:ring-3">
          <span className="text-base leading-none">{countryFlag(country.code)}</span>
          <span className="text-foreground max-w-[120px] truncate text-xs">{country.name}</span>
          <ChevronDown className="text-muted-foreground size-3" aria-hidden />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-0">
          <div className="border-border border-b p-2">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t`Search country…`}
              className="placeholder:text-muted-foreground/40 h-8 w-full rounded-md border-0 bg-transparent px-2 text-base outline-none md:text-sm"
            />
          </div>
          <div className="max-h-56 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <div className="text-muted-foreground px-2 py-3 text-center text-xs">
                <Trans>No results</Trans>
              </div>
            )}
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => {
                  onChange(c.code);
                  setOpen(false);
                  setSearch("");
                }}
                className={`hover:bg-muted/80 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                  c.code === value ? "bg-muted font-medium" : ""
                }`}
              >
                <span className="text-base leading-none">{countryFlag(c.code)}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-muted-foreground text-xs">{c.prefix}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/* ─────────────── Brand SVGs (1:1 from legacy) ─────────────── */

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function AuthIntentBanner({ intent }: { intent: AuthIntent }) {
  const { icon: Icon, title, body } = getAuthIntentCopy(intent);
  return (
    <div className="bg-primary-soft text-secondary-foreground flex items-start gap-3 rounded-lg px-3 py-2.5">
      <span className="bg-primary/15 text-primary mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full">
        <Icon className="size-4" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-foreground text-sm leading-tight font-semibold">{title}</p>
        <p className="text-muted-foreground text-xs leading-snug">{body}</p>
      </div>
    </div>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2" aria-hidden>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}
